import { ReImportDataActionStatusWithQueue } from "#batteries-included-components/Banners/ReImportDataActionStatus";
import {
  useGetOneTransaction,
  useOriginalSubmissionFileDownload,
} from "#hooks/adapters/useTransactions";
import { DataSetSubmissionDetailRoutePath } from "#routes/data-tools/submissions/datasets/[transactionId]/detail";
import {
  getStepStatusFromState,
  getTransactionTitle,
} from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionDetail.helpers";
import { DataSetSubmissionDetailsPanel } from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionDetailsPanel";
import { DataSetSubmissionRowTable } from "#routes/data-tools/submissions/datasets/[transactionId]/detail/DataSetSubmissionRowTable";
import { PageErrorContent } from "#src/batteries-included-components/Layouts/Errors/PageError";
import {
  useDownloadTemplate,
  useGetOneDataset,
} from "#src/components/hooks/adapters/useDatasets";
import { useActionDetails } from "#src/components/hooks/useActionDetails";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useMediaBreakpoint } from "#src/utils/useMediaBreakpoint";
import {
  Button,
  DropdownMenu,
  Icon,
  Page,
  PanelCollapsible,
  Pill,
  Stepper,
  useToast,
  type StepType,
} from "@validereinc/common-components";
import { Transactions } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import addDays from "date-fns/addDays";
import React, { useMemo } from "react";
import { useParams } from "react-router";
import { getTransactionStatusVariant } from "../../DataSetSubmissionsList.helpers";
import styles from "./DataSetSubmissionDetail.module.css";
import { copyTextToClipboard } from "@validereinc/utilities";

const { selectFirstAndLastTransactionState } = Transactions.logic;
const {
  DatasetSubmissionSteps,
  getActiveStepIdx,
  getStatusDisplayLabel,
  getStepDisplayLabel,
} = Transactions.view;

const cx = classNames.bind(styles);

export const DataSetSubmissionDetailPage = () => {
  const { transactionId } =
    useParams<
      Exclude<typeof DataSetSubmissionDetailRoutePath.pathParams, undefined>
    >();

  const { toast } = useToast();
  const transactionQuery = useGetOneTransaction({
    id: transactionId ?? "",
    meta: { history: true },
  });
  const transactionLastStepQuery = useGetOneTransaction(
    {
      id: transactionId ?? "",
    },
    {
      enabled: !!transactionQuery.error,
    }
  );
  const { firstTransaction, lastTransaction } =
    selectFirstAndLastTransactionState(
      transactionQuery.data?.data ?? transactionLastStepQuery.data?.data
    );
  const isExpiredTransaction =
    !!transactionQuery.error && !!transactionLastStepQuery.data?.data[0];

  const datasetQuery = useGetOneDataset({
    id:
      transactionQuery.data?.data[0].dataset_id ??
      transactionLastStepQuery.data?.data[0].dataset_id ??
      "",
  });

  const { currentBreakpointWidth, allBreakpoints } = useMediaBreakpoint();
  const { mutate: downloadTemplate, isLoading: isDownloadTemplateLoading } =
    useDownloadTemplate();

  const activeStep = useMemo<number>(
    () => (lastTransaction ? getActiveStepIdx(lastTransaction) : 1),
    [lastTransaction]
  );
  const steps = useMemo<StepType[]>(() => {
    const formattedSteps = DatasetSubmissionSteps.map<StepType>((sp, idx) => ({
      label: Array.isArray(sp)
        ? getStepDisplayLabel(sp.at(-1) ?? "transaction_recorder")
        : getStepDisplayLabel(sp),
      description:
        idx + 1 === activeStep
          ? getStatusDisplayLabel(lastTransaction?.status)
          : "",
      isBusy: idx + 1 === activeStep && lastTransaction?.status === "pending",
      intent: getStepStatusFromState(
        transactionQuery.data?.data.findLast((d) =>
          Array.isArray(sp) ? sp.some((s) => s === d.step) : d.step === sp
        ) ??
          (Array.isArray(sp)
            ? sp.some((s) => s === transactionLastStepQuery.data?.data[0].step)
              ? transactionLastStepQuery.data?.data[0]
              : undefined
            : transactionLastStepQuery.data?.data[0].step === sp
              ? transactionLastStepQuery.data?.data[0]
              : undefined)
      ),
    }));

    if (isExpiredTransaction) {
      const setStepIdx = formattedSteps.findLastIndex(
        (step) => step.intent !== "default"
      );

      if (setStepIdx !== -1) {
        return formattedSteps.map((step, idx) =>
          idx < setStepIdx ? { ...step, intent: "success" } : step
        );
      }
    }

    return formattedSteps;
  }, [
    activeStep,
    transactionQuery,
    transactionLastStepQuery,
    isExpiredTransaction,
  ]);

  const title = getTransactionTitle(lastTransaction, datasetQuery.data?.data);
  const submittedInfo = useActionDetails({
    type: "submit",
    date: firstTransaction?.created_at,
    byUserId: firstTransaction?.user_id,
  });
  const completeInfo = useActionDetails({
    type: "complete",
    date: lastTransaction?.created_at,
  });
  const metaSegments = [
    submittedInfo.description,
    completeInfo.description,
  ].filter((s) => s !== undefined);
  const [breadcrumbs] = useBreadcrumbsFromRoute(
    DataSetSubmissionDetailRoutePath,
    {
      "/detail": {
        title,
      },
    }
  );
  const downloadOriginalSubmission = useOriginalSubmissionFileDownload();

  const actionRow = [
    <Button
      key="copy-id"
      variant="outline"
      icon="copy-simple"
      disabled={!transactionId}
      onClick={() => {
        if (!transactionId) return;

        copyTextToClipboard(transactionId)
          .then(() =>
            toast.push({
              intent: "success",
              description:
                "Successfully copied data submission ID to your clipboard.",
            })
          )
          .catch(() =>
            toast.push({
              intent: "error",
              description:
                "Failed to copy data submission ID to your clipboard.",
            })
          );
      }}
    >
      Copy ID
    </Button>,
    <DropdownMenu
      key="more"
      contentProps={{
        align: "end",
      }}
      options={[
        {
          label: "Original Submission",
          isDisabled: downloadOriginalSubmission.isLoading,
          onClick: () =>
            downloadOriginalSubmission.mutate(
              {
                transactionId:
                  firstTransaction?.transaction_id ?? transactionId ?? "",
                fileName: firstTransaction?.original_file_name ?? "",
              },
              {
                onError: () => {
                  if (!isActuallyExpiredTransaction || !isExpiredTransaction)
                    return;

                  toast.push({
                    intent: "error",
                    description:
                      "Could not download the original submission because the transaction is expired (its older than 90 days)",
                  });
                },
              }
            ),
        },
        {
          label: "Dataset Template",
          isDisabled: isDownloadTemplateLoading || !datasetQuery.data?.data,
          onClick: () => {
            if (!datasetQuery.data?.data) return;

            downloadTemplate({
              datasetId: datasetQuery.data?.data?.id,
              fileName: `${datasetQuery.data?.data?.name}_template.csv`,
            });
          },
        },
      ]}
    >
      <Button
        slotRight={<Icon variant="caret-down" />}
        variant="outline"
      >
        Download
      </Button>
    </DropdownMenu>,
  ];

  const isActuallyExpiredTransaction = firstTransaction
    ? addDays(new Date(firstTransaction.created_at), 90).getTime() <
      new Date().getTime()
    : false;

  return (
    <Page
      title={
        title ? (
          <>
            {title}&nbsp;
            <Pill
              variant={getTransactionStatusVariant(lastTransaction?.status)}
              hasDot={false}
            >
              {getStatusDisplayLabel(lastTransaction?.status)}
            </Pill>
          </>
        ) : (
          DataSetSubmissionDetailRoutePath.title
        )
      }
      category={DataSetSubmissionDetailRoutePath.previous?.title}
      actionRow={actionRow}
      breadcrumbs={breadcrumbs}
      renderMeta={
        metaSegments.length === 0
          ? undefined
          : ({ MetaSegments }) => <MetaSegments values={metaSegments} />
      }
      error={
        transactionQuery.error && transactionLastStepQuery.error
          ? transactionQuery.error ||
            transactionLastStepQuery.error ||
            datasetQuery.error
          : datasetQuery.error
      }
      onErrorRender={({ error }) => <PageErrorContent error={error} />}
    >
      {firstTransaction?.transaction_id ? (
        <ReImportDataActionStatusWithQueue
          transactionId={firstTransaction?.transaction_id}
        />
      ) : null}
      {transactionId ? (
        <DataSetSubmissionDetailsPanel transactionId={transactionId} />
      ) : null}
      <div className={cx("panel-with-collapsible-panel")}>
        <PanelCollapsible
          title="Steps"
          className={cx("panel-with-collapsible-panel__panel-left")}
        >
          <Stepper
            isLoading={
              datasetQuery.isLoading ||
              (!transactionQuery.error && transactionQuery.isLoading) ||
              (!!transactionQuery.error && transactionLastStepQuery.isLoading)
            }
            activeStep={activeStep}
            orientation={
              currentBreakpointWidth >= allBreakpoints.md
                ? "vertical"
                : "horizontal"
            }
            steps={steps}
          />
        </PanelCollapsible>
        <DataSetSubmissionRowTable
          transactionStep={lastTransaction?.step}
          transactionStatus={lastTransaction?.status}
          panelProps={{
            className: cx("panel-with-collapsible-panel__panel-right"),
          }}
        />
      </div>
    </Page>
  );
};
